import React, { useState } from 'react';
import { AnchoredPortal } from './AnchoredPortal';
import {
  getTomorrowReminder,
  getNextWeekReminder,
  toDateString,
} from './reminderPopoverHelpers';
import './reminderPopover.css';

function ClockIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export interface ReminderPopoverProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  onClose: () => void;
  onSelectReminder: (due: string, time?: string) => void;
  onRemoveReminder?: () => void;
  currentDue?: string;
  currentTime?: string;
  testId?: string;
}

export const ReminderPopover: React.FC<ReminderPopoverProps> = ({
  open,
  anchorRef,
  onClose,
  onSelectReminder,
  onRemoveReminder,
  currentDue,
  currentTime,
  testId = 'reminder-popover',
}) => {
  const [showCustom, setShowCustom] = useState(false);
  const [customDate, setCustomDate] = useState(() => currentDue || toDateString(new Date()));
  const [customTime, setCustomTime] = useState(() => currentTime || '08:00');

  const tomorrow = getTomorrowReminder();
  const nextWeek = getNextWeekReminder();

  const handleSelect = (due: string, time?: string) => {
    onSelectReminder(due, time);
    onClose();
    setShowCustom(false);
  };

  const handleSaveCustom = () => {
    if (!customDate) return;
    onSelectReminder(customDate, customTime || undefined);
    onClose();
    setShowCustom(false);
  };

  const handleRemove = () => {
    onRemoveReminder?.();
    onClose();
    setShowCustom(false);
  };

  return (
    <AnchoredPortal
      open={open}
      anchorRef={anchorRef}
      onClose={() => {
        onClose();
        setShowCustom(false);
      }}
      className="z-reminder-popover-portal"
      role="dialog"
      aria-label="Recordármelo más tarde"
      data-testid={testId}
      estimatedWidth={280}
      estimatedHeight={240}
    >
      <div className="z-reminder-popover">
        <div className="z-reminder-popover-header">
          <div className="z-reminder-popover-title">Recordármelo más tarde</div>
          <div className="z-reminder-popover-subtitle">Tus recordatorios se guardan en Google Tasks</div>
        </div>

        {!showCustom ? (
          <div className="z-reminder-popover-options" role="menu">
            <button
              type="button"
              className="z-reminder-popover-item"
              role="menuitem"
              data-testid="reminder-preset-tomorrow"
              onClick={() => handleSelect(tomorrow.due, tomorrow.time)}
            >
              <span className="z-reminder-popover-item-label">{tomorrow.label}</span>
              <span className="z-reminder-popover-item-time">{tomorrow.timeLabel}</span>
            </button>

            <button
              type="button"
              className="z-reminder-popover-item"
              role="menuitem"
              data-testid="reminder-preset-next-week"
              onClick={() => handleSelect(nextWeek.due, nextWeek.time)}
            >
              <span className="z-reminder-popover-item-label">{nextWeek.label}</span>
              <span className="z-reminder-popover-item-time">{nextWeek.timeLabel}</span>
            </button>

            <button
              type="button"
              className="z-reminder-popover-item z-reminder-popover-item--custom"
              role="menuitem"
              data-testid="reminder-custom-toggle"
              onClick={() => setShowCustom(true)}
            >
              <ClockIcon />
              <span className="z-reminder-popover-item-label">Elegir fecha y hora</span>
            </button>

            {currentDue && onRemoveReminder ? (
              <button
                type="button"
                className="z-reminder-popover-item z-reminder-popover-item--remove"
                role="menuitem"
                data-testid="reminder-remove-btn"
                onClick={handleRemove}
              >
                <span className="z-reminder-popover-item-label">Eliminar recordatorio</span>
              </button>
            ) : null}
          </div>
        ) : (
          <div className="z-reminder-popover-custom" data-testid="reminder-custom-form">
            <div className="z-reminder-popover-field">
              <label htmlFor="reminder-custom-date" className="z-reminder-popover-label">
                Fecha
              </label>
              <input
                id="reminder-custom-date"
                type="date"
                className="z-reminder-popover-input"
                data-testid="reminder-custom-date"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            </div>

            <div className="z-reminder-popover-field">
              <label htmlFor="reminder-custom-time" className="z-reminder-popover-label">
                Hora
              </label>
              <input
                id="reminder-custom-time"
                type="time"
                className="z-reminder-popover-input"
                data-testid="reminder-custom-time"
                value={customTime}
                onChange={(e) => setCustomTime(e.target.value)}
              />
            </div>

            <div className="z-reminder-popover-actions">
              <button
                type="button"
                className="z-reminder-popover-btn z-reminder-popover-btn--flat"
                onClick={() => setShowCustom(false)}
              >
                Atrás
              </button>
              <button
                type="button"
                className="z-reminder-popover-btn z-reminder-popover-btn--primary"
                data-testid="reminder-custom-save"
                onClick={handleSaveCustom}
                disabled={!customDate}
              >
                Guardar
              </button>
            </div>
          </div>
        )}
      </div>
    </AnchoredPortal>
  );
};
